import { useState } from 'react';
import {
  TablePagination,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableHead,
} from '@mui/material';
import {
  DialogContainer,
  DialogBody,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { useExcelExport } from '../../hooks/useExcelExport';
import { usePDFExport } from '../../hooks/usePDFExport';
import { PAGINATION_CONFIG } from '../../constants/statisticsConstants';
import { formatCurrency, truncateText } from '../../utils/statisticsUtils';
import StatisticsDialogTitle from '../common/StatisticsDialogTitle';

const SalesReportModal = ({ open, handleClose, header, results }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_ROWS_PER_PAGE);

  const { exportToExcel } = useExcelExport();
  const { exportToPDF } = usePDFExport();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = () => {
    const data = results.map((result) => ({
      Exportación: result.export_date,
      'Número de Factura': result.invoice_number,
      CAE: result.cae,
      Contrato: result.contract_number,
      Suscriptor: result.subscriber_name,
      Cuotas: result.fees,
      Exento: result.to_exempt,
      Neto: result.to_net,
      IVA: result.to_iva,
      Total: result.to_total,
    }));

    exportToExcel(data, 'ReporteVentas');
  };

  const handleExportToPDF = () => {
    const pdfData = {
      title: 'Reporte de Ventas',
      headers: [
        'Fecha',
        'N° Factura',
        'CAE',
        'Orden',
        'Suscriptor',
        'Cuotas',
        'Exento',
        'Neto',
        'IVA',
        'Total',
      ],
      data: results.map((result) => [
        result.export_date,
        result.invoice_number,
        result.cae,
        result.contract_number,
        truncateText(result.subscriber_name, 20),
        result.fees,
        formatCurrency(result.to_exempt),
        formatCurrency(result.to_net),
        formatCurrency(result.to_iva),
        formatCurrency(result.to_total),
      ]),
      filename: 'ReporteVentas',
      orientation: 'landscape',
    };

    exportToPDF(pdfData);
  };

  if (!header || !results || results.length === 0) {
    return null;
  }

  const paginatedResults = results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <StatisticsDialogTitle
        title="Reporte de Ventas"
        onExportExcel={handleExportToExcel}
        onExportPDF={handleExportToPDF}
        onClose={handleClose}
      />
      <DialogBody>
        <Box
          sx={{ mb: 3, textAlign: 'center', border: '1px solid #4a90e2', p: 2, borderRadius: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
            {header.constructora_info}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
            Actualizado a la fecha: {header.updated_at_date}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            {header.date_filter_text}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', color: '#555' }}>
          Cantidad de facturas encontradas: {results.length}
        </Typography>

        <GeneralTableBox>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <HeadTableCell>Exportación</HeadTableCell>
                <HeadTableCell>Número de Factura</HeadTableCell>
                <HeadTableCell>CAE</HeadTableCell>
                <HeadTableCell>Contrato</HeadTableCell>
                <HeadTableCell>Suscriptor</HeadTableCell>
                <HeadTableCell>Cuotas</HeadTableCell>
                <HeadTableCell>Exento</HeadTableCell>
                <HeadTableCell>Neto</HeadTableCell>
                <HeadTableCell>IVA</HeadTableCell>
                <HeadTableCell>Total</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result, index) => (
                <TableRow key={index}>
                  <RowTableCell>{result.export_date}</RowTableCell>
                  <RowTableCell>{result.invoice_number}</RowTableCell>
                  <RowTableCell>{result.cae}</RowTableCell>
                  <RowTableCell>{result.contract_number}</RowTableCell>
                  <RowTableCell>{result.subscriber_name}</RowTableCell>
                  <RowTableCell>{result.fees}</RowTableCell>
                  <RowTableCell>{formatCurrency(result.to_exempt)}</RowTableCell>
                  <RowTableCell>{formatCurrency(result.to_net)}</RowTableCell>
                  <RowTableCell>{formatCurrency(result.to_iva)}</RowTableCell>
                  <RowTableCell>{formatCurrency(result.to_total)}</RowTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </GeneralTableBox>

        <TablePagination
          component="div"
          count={results.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Facturas por página"
          rowsPerPageOptions={PAGINATION_CONFIG.ROWS_PER_PAGE_OPTIONS}
        />
      </DialogBody>
    </DialogContainer>
  );
};

export default SalesReportModal;
